<template>
  <div>
    <BoxComponent :mb="24">
      <FlexComponent align="center" justify="space-between" :gap="8" wrap="wrap">
        <SearchBox
          placeholder="Search influencer.."
          name="usersSearch"
          :on-change="changeSearchQuery"
          class="influencers-table__search-box"
        />
        <ButtonComponent variant="accent" class="influencers-table__export-btn" @click="exportStats">
          <template #iconLeft>
            <el-icon :size="18">
              <DownloadIcon />
            </el-icon>
          </template>
          Export Stats
        </ButtonComponent>
      </FlexComponent>
    </BoxComponent>
    <Table :pagination="pagination" :pages-with-data="pagesWithData" :loading="loading" :change-page="changePage">
      <template #thead>
        <tr>
          <TableHeader label="Creator" column-attribute="username" :sort-by="sortBy" :sort-order="sortOrder" :on-update-sort="changeSort" />
          <TableHeader v-if="!hideEdit" label="Commission" />
          <TableHeader
            label="Instagram followers"
            column-attribute="instagram_followers"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            label="Clicks"
            column-attribute="clicks"
            align="right"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            v-if="!hideCosts"
            label="Cost"
            align="right"
            column-attribute="store_cost"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            v-if="!hideSales"
            label="Order value"
            align="right"
            column-attribute="order_value"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            v-if="!(hideSales || hideCosts)"
            label="ROAS"
            align="right"
            column-attribute="roas"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
          <TableHeader
            v-if="!hideSales"
            label="Nr orders"
            align="right"
            column-attribute="nr_orders"
            :sort-by="sortBy"
            :sort-order="sortOrder"
            :on-update-sort="changeSort"
          />
        </tr>
      </template>
      <template #tbody>
        <tr v-for="(user, key) in pagesWithData[pagination.currentPage]" :key="key">
          <td class="align-left influencers-table__username">
            <router-link v-if="!hideSales || !hideCosts" :to="{ path: `/creators/${user.id}` }">
              <TypographyComponent variant="label-large">{{ user.username }}</TypographyComponent>
            </router-link>
            <TypographyComponent v-else variant="label-large">{{ user.username }}</TypographyComponent>
            <BoxComponent :mt="8">
              <FlexComponent :gap="8" wrap="wrap">
                <TypographyComponent
                  v-for="item in user.userTags"
                  :key="item.id"
                  variant="label-small"
                  class="influencers-table__user-tag"
                  >{{ item.name }}</TypographyComponent
                >
              </FlexComponent>
            </BoxComponent>
          </td>
          <td v-if="!hideEdit" class="cost-cell align-left">
            <CostCell
              v-if="'traffic_sources_costs' in user"
              :traffic-sources="user.traffic_sources_costs"
              :base-traffic-sources="shopTrafficSources"
              :use-cpa="advertiserUsesCpa"
              :on-save="(costState) => saveCostChange(user, costState)"
            />
          </td>
          <td class="align-left">
            {{ formatNumber(user.instagramFollowers) }}
          </td>
          <td class="align-right">
            {{ formatNumber(user.clicks) }}
          </td>
          <td v-if="!hideCosts" class="align-right">
            {{ formatMoney(user.cost) }}
          </td>
          <td v-if="!hideSales" class="align-right">
            {{ 'orderValue' in user ? formatMoney(user.orderValue) : '' }}
          </td>
          <td v-if="!(hideSales || hideCosts)" class="align-right">
            {{ user.roas || '-' }}
          </td>
          <td v-if="!hideSales" class="align-right">
            {{ 'nrOfOrders' in user ? formatNumber(user.nrOfOrders) : '' }}
          </td>
        </tr>
      </template>
    </Table>
  </div>
</template>

<script lang="ts">
import { ButtonComponent } from '@metapic-apps/shared'
import { ElNotification } from 'element-plus'
import { mapActions, mapGetters } from 'vuex'

import DownloadIcon from '@/assets/icons/DownloadStoryIcon.svg?component'
import CostCell from '@/components/costs/CostCell.vue'
import SearchBox from '@/components/SearchBox.vue'
import Table from '@/components/table/Table.vue'
import TableHeader from '@/components/table/TableHeader.vue'
import { RepositoryFactory } from '@/legacy/repositories/RepositoryFactory'
import storeRepository from '@/legacy/repositories/storeRepository'
import { formatMoney, formatNumber } from '@/legacy/store/helpFunctions'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const StoreRepository = RepositoryFactory.get('store')

export default {
  name: 'InfluencersTable',
  components: {
    TypographyComponent,
    BoxComponent,
    FlexComponent,
    DownloadIcon,
    ButtonComponent,
    SearchBox,
    CostCell,
    // eslint-disable-next-line vue/no-reserved-component-names
    Table,
    TableHeader,
  },
  data() {
    return {
      editingCpc: {},
    }
  },
  computed: {
    ...mapGetters('user', ['user', 'dataDateRange', 'token']),
    ...mapGetters('shop', ['shop', 'hideClicks', 'hideEdit', 'hideSales', 'hideCosts', 'shopTrafficSources', 'advertiserUsesCpa']),
    ...mapGetters('channelInfluencers', ['pagesWithData', 'loading', 'sortBy', 'sortOrder', 'searchQuery', 'pagination']),
    ...mapGetters('userTags', ['userTags', 'userTagsWithCampaigns']),
  },
  mounted() {
    this.initModule()
  },
  methods: {
    ...mapActions('channelInfluencers', ['changePage', 'changeSort', 'changeSearchQuery', 'setPageData', 'initModule']),
    ...mapActions('userTags', ['fetchUserTags']),
    saveCostChange(user, costState) {
      const traffic_sources_costs = []
      for (let i = 0; i < costState.length; i++) {
        const temp: {
          source: unknown
          cpc?: number
          cpa?: number
        } = {
          source: costState[i].source,
        }
        if (costState[i].cpc) {
          temp.cpc = Number((costState[i].cpc.toFixed(2) * 100).toFixed(0))
        } else {
          temp.cpc = 0
        }
        if (costState[i].cpa) {
          temp.cpa = Number((costState[i].cpa.toFixed(0) / 100).toFixed(2))
        } else {
          temp.cpa = 0
        }
        traffic_sources_costs.push(temp)
      }

      const pageWithUser = this.pagination.currentPage
      const sortingAndFilterOnSave = {
        sortBy: this.sortBy,
        sortOrder: this.sortOrder,
        searchQuery: this.searchQuery,
      }

      StoreRepository.updateCostsOnUser(this.token, this.shop.id, user.id, {
        traffic_sources_costs: traffic_sources_costs,
      }).then(({ data }) => {
        // Update user in table
        const sortingAndFilterUnChanged =
          sortingAndFilterOnSave.sortBy === this.sortBy &&
          sortingAndFilterOnSave.sortOrder === this.sortOrder &&
          sortingAndFilterOnSave.searchQuery === this.searchQuery
        const pageWithData = pageWithUser < this.pagesWithData.length && this.pagesWithData[pageWithUser]

        if (sortingAndFilterUnChanged && pageWithData) {
          const indexOfUser = pageWithData.findIndex((u) => u === user)
          if (indexOfUser > -1) {
            pageWithData[indexOfUser] = this.getUserUpdatedWithCosts(user, data)
            this.setPageData({
              page: pageWithUser,
              data: [...pageWithData],
            })
          }
        }

        ElNotification({
          title: 'Success!',
          message: 'Your changes have been saved',
          type: 'success',
        })
      })
    },
    getUserUpdatedWithCosts(user, campaignFromApi) {
      if (campaignFromApi?.traffic_sources_costs) {
        return { ...user, traffic_sources_costs: campaignFromApi.traffic_sources_costs }
      }

      return { ...user }
    },
    async exportStats() {
      await storeRepository
        .getUserStats(
          this.token,
          this.shop.id,
          { from: this.dataDateRange.start, to: this.dataDateRange.end },
          1,
          this.sortBy,
          this.sortOrder,
          this.searchQuery,
          true,
        )
        .then(({ data }) => {
          const blob = new Blob([data], {
            type: 'application/vnd.ms-excel',
          })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = 'user-stats.xlsx'
          link.click()
          URL.revokeObjectURL(link.href)
        })
    },
    formatMoney,
    formatNumber,
  },
}
</script>

<style lang="scss" scoped>
.influencers-table__search-box {
  width: 320px;
}
.influencers-table__username {
  max-width: 250px;
  text-overflow: ellipsis;
  overflow: hidden;
}
.influencers-table__user-tag {
  padding: 4px 8px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}
.search-filter-section {
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
}

.search-filter-section > div {
  display: flex;
}
</style>
